import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { History, X } from 'lucide-react';
import { UcParaRateio } from '../../../src/hooks/useRateioBalanceamento';

interface UcHistoricoPopoverProps {
  anchor: DOMRect;
  onClose: () => void;
  uc: UcParaRateio;
}

const POPOVER_W = 280;

const formatData = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : null;

const MOTIVO_LABEL: Record<string, string> = {
  excesso_saldo: 'Travamento automático — excesso de saldo (cobertura > 3 meses)',
  manual: 'Pausada manualmente',
};

/**
 * Histórico mínimo de travamento/destravamento da UC (Seção 11 da spec — "Ver
 * Histórico" no kebab). Só os eventos de trava/destrava desta fatia — não é o
 * log de auditoria completo de mudanças de rateio da Seção 3 (fatia futura).
 */
const UcHistoricoPopover: React.FC<UcHistoricoPopoverProps> = ({ anchor, onClose, uc }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: anchor.bottom + 6, left: anchor.left });

  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight || 160;
    const margin = 8;
    let top = anchor.bottom + 6;
    if (top + h > window.innerHeight - margin) top = Math.max(margin, anchor.top - h - 6);
    let left = anchor.left - POPOVER_W + anchor.width;
    if (left + POPOVER_W > window.innerWidth - margin) left = window.innerWidth - POPOVER_W - margin;
    if (left < margin) left = margin;
    setPos({ top, left });
  }, [anchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const travadoEm = formatData(uc.rateio_travado_em);
  const destravadoEm = formatData(uc.rateio_destravado_em);

  return createPortal(
    <div className="fixed inset-0 z-[2100]" onClick={onClose}>
      <div
        ref={ref}
        style={{ top: pos.top, left: pos.left, width: POPOVER_W }}
        className="fixed bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-4 animate-fade-in"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-1.5">
            <History size={14} className="text-gray-400" /> Histórico de Travamento
          </h4>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={16} />
          </button>
        </div>

        {!travadoEm && !destravadoEm ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">Esta UC nunca foi travada ou destravada.</p>
        ) : (
          <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-2">
            {travadoEm && (
              <li>
                <span className="block font-medium text-gray-800 dark:text-gray-100">Travada em {travadoEm}</span>
                <span className="text-gray-500 dark:text-gray-400">
                  {(uc.rateio_motivo_travamento && MOTIVO_LABEL[uc.rateio_motivo_travamento]) || 'Motivo não registrado.'}
                </span>
              </li>
            )}
            {destravadoEm && (
              <li>
                <span className="block font-medium text-gray-800 dark:text-gray-100">Destravada em {destravadoEm}</span>
                <span className="text-gray-500 dark:text-gray-400">Liberada por um Gestor.</span>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>,
    document.body
  );
};

export default UcHistoricoPopover;
